<script setup lang="ts">
/**
 * "Add to Findings" dialog, opened from the "Add to findings" action on a
 * single agent response. Collects a free-text description and, optionally, the
 * narrative to file the finding under, and records it as a finding.
 *
 * The narrative field is a select-or-create combobox: the user can pick an
 * existing narrative from the list or type a new name to create one. Linking is
 * optional — leaving it blank records an unfiled finding.
 *
 * The parent owns the submit (posting through the findings store, creating the
 * narrative first when the name is new) and the busy/close lifecycle; this
 * component is a controlled dialog that emits the description plus the chosen
 * narrative (an existing id, or a new title to create).
 */
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

/** A narrative the finding can be filed under (id + display title). */
export interface NarrativeOption {
    id: string;
    title: string;
}

/** What the dialog resolves the narrative field to on submit. */
export interface FindingSubmit {
    description: string;
    // Set when filing under an existing narrative.
    narrativeId: string | null;
    // Set when the user typed a name with no matching narrative — the parent
    // creates it, then links the finding to the new id.
    newNarrativeTitle: string | null;
}

const props = defineProps<{
    visible: boolean;
    // The response's reference count, shown as the evidence-items line. Display
    // only for now (the backend doesn't yet link evidence to a finding); the
    // line is hidden when the count isn't known or is zero.
    evidenceCount?: number;
    // The matter's existing narratives, offered in the combobox.
    narratives?: NarrativeOption[];
    // True while the create request is in flight — disables the submit button.
    submitting?: boolean;
}>();

const emit = defineEmits<{
    submit: [payload: FindingSubmit];
    close: [];
}>();

const description = ref('');

// Narrative combobox state. `narrativeInput` is the text in the field;
// `selectedId` is set only when the user picks an existing narrative from the
// list (typing after a pick clears it, so the input is treated as a new name).
const narrativeInput = ref('');
const selectedId = ref<string | null>(null);
const showOptions = ref(false);

const options = computed<NarrativeOption[]>(() => props.narratives ?? []);

// Existing narratives whose title contains the query (case-insensitive). With
// an empty query the whole list is offered.
const filteredOptions = computed<NarrativeOption[]>(() => {
    const q = narrativeInput.value.trim().toLowerCase();
    if (!q) return options.value;
    return options.value.filter((n) => n.title.toLowerCase().includes(q));
});

// An existing narrative whose title exactly matches the typed text (used to
// avoid creating a duplicate when the user types a name instead of clicking it).
const exactMatch = computed<NarrativeOption | undefined>(() => {
    const q = narrativeInput.value.trim().toLowerCase();
    if (!q) return undefined;
    return options.value.find((n) => n.title.toLowerCase() === q);
});

// Offer a "create new" row when the text is non-empty and names no existing
// narrative exactly.
const showCreateRow = computed(
    () => narrativeInput.value.trim().length > 0 && !exactMatch.value,
);

// Reset the draft each time the dialog opens so it never reopens with the
// previous response's text or narrative selection.
watch(
    () => props.visible,
    (open) => {
        if (open) {
            description.value = '';
            narrativeInput.value = '';
            selectedId.value = null;
            showOptions.value = false;
        }
    },
);

const onNarrativeInput = () => {
    // Typing means the field no longer reflects a picked narrative.
    selectedId.value = null;
    showOptions.value = true;
};

const pickNarrative = (option: NarrativeOption) => {
    selectedId.value = option.id;
    narrativeInput.value = option.title;
    showOptions.value = false;
};

const clearNarrative = () => {
    selectedId.value = null;
    narrativeInput.value = '';
    showOptions.value = false;
};

const onNarrativeFocus = () => {
    showOptions.value = true;
};

const onNarrativeBlur = () => {
    // Delay so a click on an option registers before the list is hidden.
    setTimeout(() => {
        showOptions.value = false;
    }, 120);
};

const resolveNarrative = (): { narrativeId: string | null; newNarrativeTitle: string | null } => {
    const text = narrativeInput.value.trim();
    if (!text) return { narrativeId: null, newNarrativeTitle: null };
    // Picked from the list (and not since edited).
    if (selectedId.value) return { narrativeId: selectedId.value, newNarrativeTitle: null };
    // Typed a name matching an existing narrative — link to it, don't duplicate.
    if (exactMatch.value) return { narrativeId: exactMatch.value.id, newNarrativeTitle: null };
    // A genuinely new name.
    return { narrativeId: null, newNarrativeTitle: text };
};

const onSubmit = () => {
    const text = description.value.trim();
    if (!text || props.submitting) return;
    emit('submit', { description: text, ...resolveNarrative() });
};

const onClose = () => emit('close');

// Focus management: when the create row is chosen via keyboard Enter, keep the
// typed value (no-op — resolveNarrative handles it). Exposed for template use.
const selectCreateRow = async () => {
    showOptions.value = false;
    await nextTick();
};
</script>

<template>
    <div v-if="visible" class="findings-dialog-overlay" @click.self="onClose">
        <div
            class="findings-dialog"
            role="dialog"
            aria-modal="true"
            :aria-label="t('chat.findingsTitle')"
        >
            <div class="findings-dialog-head">
                <h3 class="findings-dialog-title">
                    <span class="material-icons-outlined" aria-hidden="true">bookmark_border</span>
                    {{ t('chat.findingsTitle') }}
                </h3>
                <button
                    type="button"
                    class="findings-dialog-close"
                    :aria-label="t('common.close')"
                    @click="onClose"
                >
                    <span class="material-icons-outlined">close</span>
                </button>
            </div>

            <i18n-t
                v-if="evidenceCount"
                keypath="chat.findingsEvidence"
                tag="p"
                class="findings-dialog-evidence"
                :plural="evidenceCount"
            >
                <template #count><strong>{{ evidenceCount }}</strong></template>
            </i18n-t>

            <label class="findings-dialog-label" for="finding-description">{{
                t('chat.findingsDescription')
            }}</label>
            <textarea
                id="finding-description"
                v-model="description"
                class="findings-dialog-textarea"
                :placeholder="t('chat.findingsDescriptionPlaceholder')"
                rows="4"
            ></textarea>

            <label class="findings-dialog-label" for="finding-narrative">{{
                t('chat.findingsNarrativeLabel')
            }}</label>
            <div class="findings-dialog-combo">
                <input
                    id="finding-narrative"
                    v-model="narrativeInput"
                    type="text"
                    class="findings-dialog-combo-input"
                    :placeholder="t('chat.findingsNarrativePlaceholder')"
                    autocomplete="off"
                    role="combobox"
                    aria-autocomplete="list"
                    :aria-expanded="showOptions"
                    @input="onNarrativeInput"
                    @focus="onNarrativeFocus"
                    @blur="onNarrativeBlur"
                />
                <button
                    v-if="narrativeInput"
                    type="button"
                    class="findings-dialog-combo-clear"
                    :aria-label="t('common.close')"
                    @mousedown.prevent="clearNarrative"
                >
                    <span class="material-icons-outlined">close</span>
                </button>

                <ul v-if="showOptions" class="findings-dialog-combo-list" role="listbox">
                    <li
                        v-for="option in filteredOptions"
                        :key="option.id"
                        class="findings-dialog-combo-option"
                        :class="{ 'is-selected': option.id === selectedId }"
                        role="option"
                        :aria-selected="option.id === selectedId"
                        @mousedown.prevent="pickNarrative(option)"
                    >
                        <span class="material-icons-outlined" aria-hidden="true">description</span>
                        {{ option.title }}
                    </li>
                    <li
                        v-if="showCreateRow"
                        class="findings-dialog-combo-option findings-dialog-combo-create"
                        role="option"
                        @mousedown.prevent="selectCreateRow"
                    >
                        <span class="material-icons-outlined" aria-hidden="true">add</span>
                        {{ t('chat.findingsNarrativeCreate', { title: narrativeInput.trim() }) }}
                    </li>
                    <li
                        v-if="!filteredOptions.length && !showCreateRow"
                        class="findings-dialog-combo-empty"
                    >
                        {{ t('chat.findingsNarrativeEmpty') }}
                    </li>
                </ul>
            </div>

            <div class="findings-dialog-actions">
                <button
                    type="button"
                    class="findings-dialog-submit"
                    :disabled="!description.trim() || submitting"
                    @click="onSubmit"
                >
                    {{ submitting ? t('chat.addingFinding') : t('chat.addFinding') }}
                </button>
            </div>
        </div>
    </div>
</template>
